// NexumPaySummaryCard.jsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { numberFormat } from "../../util/araknerdlib";

import { Skeleton, SkeletonBar, SkeletonCircle, SkeletonPill } from "../../util/skeleton/Skeleton";
/* -------------------------------------------
 * Skeleton that mimics the exact NexumPay card
 * ------------------------------------------ */
const NexumPaySummarySkeleton = () => {
  return (
    <div
      className="card border-0 shadow-sm mt-3"
      style={{
        borderRadius: 12,
        transition: "box-shadow .2s, transform .2s",
      }}
    >
      <div className="card-body d-flex flex-column">
        {/* Header */}
        <div className="d-flex align-items-start mb-1">
          <span
            className="rounded d-inline-flex align-items-center justify-content-center mr-3"
            style={{ width: 44, height: 44, background: "#EEF2F6" }}
          >
            <SkeletonCircle size={18} />
          </span>

          <div className="flex-grow-1">
            <Skeleton w="120px" h={14} />
            <div style={{ marginTop: 6 }}>
              <Skeleton w="180px" h={12} />
            </div>
          </div>

          <span className="badge badge-light" style={{ borderRadius: 999 }}>
            <SkeletonPill w={84} h={14} />
          </span>
        </div>

        {/* Available / Pending */}
        <div className="row mt-2">
          <div className="col-6">
            <Skeleton w="70px" h={12} />
            <div style={{ marginTop: 8 }}>
              <Skeleton w="150px" h={18} />
            </div>
          </div>
          <div className="col-6 text-right">
            <Skeleton w="60px" h={12} style={{ marginLeft: "auto" }} />
            <div style={{ marginTop: 8, marginLeft: "auto" }}>
              <Skeleton w="140px" h={18} style={{ marginLeft: "auto" }} />
            </div>
          </div>
        </div>

        {/* Today volume + progress */}
        <div className="mt-3">
          <div className="d-flex justify-content-between mb-1">
            <Skeleton w="110px" h={12} />
            <Skeleton w="120px" h={12} />
          </div>

          <div
            className="progress"
            style={{
              height: 6,
              background: "rgba(0,0,0,0.06)",
              borderRadius: 9999,
              overflow: "hidden",
            }}
          >
            <div style={{ width: "100%" }}>
              <SkeletonBar h={6} />
            </div>
          </div>
        </div>

        {/* Wallets + Next payout */}
        <div className="mt-3 d-flex justify-content-between">
          <Skeleton w="90px" h={12} />
          <Skeleton w="170px" h={12} />
        </div>

        {/* Actions */}
        <div className="mt-3 d-flex justify-content-between">
          <SkeletonPill w={140} h={34} />
          <SkeletonPill w={150} h={34} />
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------
 * Empty-state (not connected)
 * ------------------------------------------ */
const NexumPayNotConnected = ({ onConnect = () => {} }) => {
  return (
    <div
      className="card border-0 shadow-sm mt-3"
      style={{ transition: "box-shadow .2s, transform .2s" }}
    >
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-start mb-1">
          <span
            className="rounded d-inline-flex align-items-center justify-content-center mr-3"
            style={{ width: 44, height: 44, background: "#EEF2F6" }}
          >
            <i className="fa fa-wallet text-muted" />
          </span>

          <div className="flex-grow-1">
            <div className="h6 mb-0">NexumPay</div>
            <div className="text-muted small">Payment Wallets Summary</div>
          </div>

          <span className="badge badge-secondary" style={{ borderRadius: 999 }}>
            Not Connected
          </span>
        </div>

        {/* Soft “blanks” that still look intentional */}
        <div
          className="mt-1 p-3"
          style={{
            borderRadius: 12,
            border: "1px dashed rgba(0,0,0,0.10)",
            background: "rgba(0,0,0,0.02)",
          }}
        >
          <div className="d-flex align-items-start">
            <span
              className="d-inline-flex align-items-center justify-content-center mr-2"
              style={{
                width: 28,
                height: 28,
                borderRadius: 10,
                background: "rgba(0,0,0,0.04)",
              }}
            >
              <i className="fa fa-plug text-muted" />
            </span>

            <div style={{ flex: 1 }}>
              <div className="font-weight-semibold" style={{ fontSize: 13 }}>
                Connect NexumPay to view balances
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                Once connected, you’ll see Available, Pending, today’s volume and payout schedule here.
              </div>
            </div>
          </div>
        </div>

        {/* Disabled-looking stats row */}
        {/* <div className="row mt-3">
          <div className="col-6">
            <div className="text-muted small">Available</div>
            <div className="h6 mb-0 text-muted">—</div>
          </div>
          <div className="col-6 text-right">
            <div className="text-muted small">Pending</div>
            <div className="h6 mb-0 text-muted">—</div>
          </div>
        </div> */}

        <div className="mt-3">
          <div className="d-flex justify-content-between small text-muted mb-0">
            <span>Today’s Volume</span>
            <span>—</span>
          </div>
          <div className="progress" style={{ height: 6, opacity: 0.45 }}>
            <div className="progress-bar bg-secondary" style={{ width: "0%" }} />
          </div>
        </div>

        <div className="mt-2 d-flex justify-content-between">
          <button className="btn btn-sm btn-primary" onClick={onConnect}>
            <i className="fa fa-link mr-1" />
            Connect NexumPay
          </button>

          <Link to="/banking-nexumpay" className="btn btn-sm btn-outline-primary">
            <i className="fa fa-external-link-alt mr-1" />
            Learn More
          </Link>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------
 * Main card
 * ------------------------------------------ */
const NexumPaySummaryCard = ({
  currency = "UGX",
  loading = false,

  // connection state
  isConnected = true,
  onConnect = () => {},

  // summary data (pass from API; keeping your dummy example fallback)
  summary = null,
}) => {
  const safeSummary = useMemo(
    () =>
      summary || {
        available: 51_400_000,
        pending: 7_150_000,
        todayVolume: 5_800_000,
        todayProgress: 58,
        walletCount: 2,
        nextPayout: "Today 6:00 PM",
        onCollect: () => {},
      },
    [summary]
  );

  if (loading) return <NexumPaySummarySkeleton />;

  if (!isConnected) return <NexumPayNotConnected onConnect={onConnect} />;

  return (
    <div
      className="card border-0 shadow-sm mt-3"
      style={{ transition: "box-shadow .2s, transform .2s" }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "none")}
    >
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-start mb-1">
          <span
            className="rounded d-inline-flex align-items-center justify-content-center mr-3"
            style={{ width: 44, height: 44, background: "#EEF2F6" }}
          >
            <i className="fa fa-wallet" />
          </span>

          <div className="flex-grow-1">
            <div className="h6 mb-0">NexumPay</div>
            <div className="text-muted small">Payment Wallets Summary</div>
          </div>

          <span className="badge badge-success" style={{ borderRadius: 999 }}>
            CONNECTED
          </span>
        </div>

        <div className="row mt-2">
          <div className="col-6">
            <div className="text-muted small">Available</div>
            <div className="h6 mb-0">
              {currency} {numberFormat(safeSummary.available)}
            </div>
          </div>

          <div className="col-6 text-right">
            <div className="text-muted small">Pending</div>
            <div className="h6 mb-0">
              {currency} {numberFormat(safeSummary.pending)}
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="d-flex justify-content-between small text-muted mb-1">
            <span>Today’s Volume</span>
            <span>
              {currency} {numberFormat(safeSummary.todayVolume)}
            </span>
          </div>

          <div className="progress" style={{ height: 6 }}>
            <div className="progress-bar bg-info" style={{ width: `${safeSummary.todayProgress}%` }} />
          </div>
        </div>

        <div className="mt-3 small text-muted d-flex justify-content-between">
          <span>Wallets: {safeSummary.walletCount}</span>
          <span>Next Payout: {safeSummary.nextPayout}</span>
        </div>

        <div className="mt-3 d-flex justify-content-between">
          <button className="btn btn-sm btn-light" onClick={safeSummary.onCollect}>
            <i className="fa fa-arrow-up mr-1" />
            Receive Payment
          </button>

          <Link to="/banking-nexumpay" className="btn btn-sm btn-outline-primary">
            <i className="fa fa-external-link-alt mr-1" />
            Open NexumPay
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NexumPaySummaryCard;
